export const OPPORTUNITIES_TABLE =
  "ALLGRANTS&SAVINGS(Automated-AccountsReceivable)";

export const MANAGED_CLIENT_GROUP_PREFIX = "group:";

export const MANAGED_CLIENT_GROUP_OPTIONS = [
  { value: "group:MA", label: "Managed Clients - Active", isGroup: true },
  { value: "group:MP", label: "Managed Clients - Paused", isGroup: true },
  { value: "group:MC", label: "Managed Clients - Churned", isGroup: true },
  { value: "group:ALL", label: "Managed Clients - All", isGroup: true },
];

export function isManagedClientGroupFilter(value) {
  return (
    typeof value === "string" && value.startsWith(MANAGED_CLIENT_GROUP_PREFIX)
  );
}

export function getManagedClientGroupKey(value) {
  if (!isManagedClientGroupFilter(value)) return null;
  return value.slice(MANAGED_CLIENT_GROUP_PREFIX.length);
}

export function getManagedClientGroupLabel(value) {
  const option = MANAGED_CLIENT_GROUP_OPTIONS.find((o) => o.value === value);
  return option?.label || value;
}

/** Filter companies table records by ClientType bucket. */
export function buildCompaniesClientTypeFilterFormula(groupKey) {
  switch (groupKey) {
    case "MA":
      return '{ClientType} = "MA"';
    case "MP":
      return '{ClientType} = "MP"';
    case "MC":
      return '{ClientType} = "MC"';
    case "ALL":
      return 'AND({ClientType} != "", {ClientType} != BLANK())';
    default:
      return null;
  }
}

/** Match opportunities by CompanyID (preferred for managed-client group filters). */
export function buildCompanyIdFilterCondition(companyIds) {
  if (!companyIds?.length) {
    return "FALSE()";
  }

  if (companyIds.length === 1) {
    return `{CompanyID} = '${companyIds[0]}'`;
  }

  const batchSize = 10;
  if (companyIds.length <= batchSize) {
    return `OR(${companyIds.map((id) => `{CompanyID} = '${id}'`).join(",")})`;
  }

  const batchFormulas = [];
  for (let i = 0; i < companyIds.length; i += batchSize) {
    const batch = companyIds.slice(i, i + batchSize);
    batchFormulas.push(
      `OR(${batch.map((id) => `{CompanyID} = '${id}'`).join(",")})`
    );
  }

  return `OR(${batchFormulas.join(",")})`;
}

/** Case-insensitive exact match on opportunities CompanyName column. */
export function buildCompanyNameFilterCondition(companyName) {
  const escaped = companyName.replace(/"/g, '\\"');
  return `LOWER({CompanyName}) = LOWER("${escaped}")`;
}

/** ClientType codes used in PrioritizedView scope toggle. */
export const PRIORITIZED_CLIENT_TYPE_FILTER_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "MA", label: "MA", title: "Managed — Active" },
  { value: "MP", label: "MP", title: "Managed — Paused" },
  { value: "MC", label: "MC", title: "Managed — Churned" },
  { value: "CA", label: "CA", title: "Alpha — Active" },
  { value: "CP", label: "CP", title: "Alpha — Paused" },
  { value: "CC", label: "CC", title: "Alpha — Churned" },
];

/** Companies included when loading PrioritizedView opportunities. */
export function buildPrioritizedScopeCompaniesFormula() {
  return 'OR({ManagedClient} = "true", OR({ClientType} = "CA", {ClientType} = "CP", {ClientType} = "CC"))';
}

export function companyIdFromOpportunityFields(fields = {}) {
  const raw = fields.CompanyID;
  if (Array.isArray(raw)) return raw[0] || null;
  return raw || null;
}

export function buildCompanyClientTypeLookups(companies = []) {
  const byId = {};
  const byName = {};

  companies.forEach((company) => {
    const clientType = String(company.fields?.ClientType || "")
      .trim()
      .toUpperCase();
    if (company.id) {
      byId[company.id] = clientType;
    }

    const name = company.fields?.Name?.trim();
    if (name) {
      byName[name.toLowerCase()] = clientType;
    }
  });

  return { byId, byName };
}

export function getOpportunityClientType(opportunity, lookups = {}) {
  const fields = opportunity?.fields || {};
  const { byId = {}, byName = {} } = lookups;

  const companyId = companyIdFromOpportunityFields(fields);
  if (companyId && byId[companyId]) {
    return byId[companyId];
  }

  const companyName = fields.CompanyName?.trim();
  if (companyName && byName[companyName.toLowerCase()]) {
    return byName[companyName.toLowerCase()];
  }

  return null;
}

export function opportunityMatchesClientTypeFilter(
  opportunity,
  filter,
  lookups = {}
) {
  if (!filter || filter === "ALL") return true;

  const clientType = getOpportunityClientType(opportunity, lookups);
  if (!clientType) return false;

  return clientType === filter;
}

export function dedupeCompanyNames(records, getName) {
  const seen = new Map();

  records.forEach((record) => {
    const name = getName(record)?.trim();
    if (!name) return;

    const key = name.toLowerCase();
    if (seen.has(key)) return;

    seen.set(key, {
      value: name,
      label: name,
    });
  });

  return Array.from(seen.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
}
